// Browser-only local outbox for offline expense creation. Pending expenses are
// stored in IndexedDB and flushed to the server when connectivity returns.
import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import type { CreateExpenseInput } from "@/lib/receipt";

export type PendingExpense = CreateExpenseInput & {
  localId: string;
  queuedAt: string; // ISO
};

interface ExpenseDB extends DBSchema {
  outbox: { key: string; value: PendingExpense };
}

const DB_NAME = "expense-tracker";
const STORE = "outbox";
const CHANGE_EVENT = "outbox-change";

let dbPromise: Promise<IDBPDatabase<ExpenseDB>> | null = null;

function db() {
  if (!dbPromise) {
    dbPromise = openDB<ExpenseDB>(DB_NAME, 1, {
      upgrade(d) {
        if (!d.objectStoreNames.contains(STORE)) {
          d.createObjectStore(STORE, { keyPath: "localId" });
        }
      },
    });
  }
  return dbPromise;
}

export async function queueExpense(
  input: CreateExpenseInput,
): Promise<PendingExpense> {
  const item: PendingExpense = {
    ...input,
    localId: crypto.randomUUID(),
    queuedAt: new Date().toISOString(),
  };
  await (await db()).put(STORE, item);
  notifyChange();
  return item;
}

export async function getPending(): Promise<PendingExpense[]> {
  const all = await (await db()).getAll(STORE);
  return all.sort((a, b) => a.queuedAt.localeCompare(b.queuedAt));
}

export async function removePending(localId: string): Promise<void> {
  await (await db()).delete(STORE, localId);
  notifyChange();
}

export async function countPending(): Promise<number> {
  return (await db()).count(STORE);
}

// Lightweight pub/sub so the sync pill updates the moment the outbox changes.
export function notifyChange() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }
}

export function onOutboxChange(cb: () => void): () => void {
  window.addEventListener(CHANGE_EVENT, cb);
  return () => window.removeEventListener(CHANGE_EVENT, cb);
}
